// =============================================================================
// FIELD CONFIG PANEL - Configuration panel for selected field
// =============================================================================

import { X, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Separator } from '../../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useFormBuilderStore } from '../store';
import type { FormField, FieldOption } from '../types';

export function FieldConfigPanel() {
  const { sections, selectedFieldId, selectField, updateField, addValidation, removeValidation } =
    useFormBuilderStore();

  // Find selected field across all sections
  let selectedField: FormField | undefined;
  let sectionId: string | undefined;

  for (const section of sections) {
    const field = section.fields.find((f) => f.id === selectedFieldId);
    if (field) {
      selectedField = field;
      sectionId = section.id;
      break;
    }
  }

  if (!selectedField || !sectionId) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-center p-6">
        <p className="text-secondary-500">Select a field to configure</p>
      </div>
    );
  }

  const handleUpdate = (updates: Partial<FormField>) => {
    updateField(sectionId!, selectedField!.id, updates);
  };

  const handleAddOption = () => {
    const newOptions = [
      ...(selectedField?.options || []),
      { label: `Option ${(selectedField?.options?.length || 0) + 1}`, value: `option${Date.now()}` },
    ];
    handleUpdate({ options: newOptions });
  };

  const handleUpdateOption = (index: number, updates: Partial<FieldOption>) => {
    const newOptions = [...(selectedField?.options || [])];
    const current = newOptions[index];
    if (current) {
      newOptions[index] = { ...current, ...updates };
      handleUpdate({ options: newOptions });
    }
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = (selectedField?.options || []).filter((_, i) => i !== index);
    handleUpdate({ options: newOptions });
  };

  const hasOptions = ['select', 'multiselect', 'radio'].includes(selectedField.type);
  const isRequired = selectedField.validation.some((v) => v.type === 'required');

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
        <h3 className="font-semibold text-secondary-900 dark:text-white">
          Field Settings
        </h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => selectField(null)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Basic info */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Label</Label>
            <Input
              value={selectedField.label}
              onChange={(e) => handleUpdate({ label: e.target.value })}
              placeholder="Field label"
            />
          </div>

          <div className="space-y-2">
            <Label>Field Name</Label>
            <Input
              value={selectedField.name}
              onChange={(e) => handleUpdate({ name: e.target.value })}
              placeholder="field_name"
            />
            <p className="text-xs text-secondary-500">
              Used in form submissions
            </p>
          </div>

          <div className="space-y-2">
            <Label>Placeholder</Label>
            <Input
              value={selectedField.placeholder || ''}
              onChange={(e) => handleUpdate({ placeholder: e.target.value })}
              placeholder="Enter placeholder text"
            />
          </div>

          <div className="space-y-2">
            <Label>Help Text</Label>
            <Textarea
              value={selectedField.helpText || ''}
              onChange={(e) => handleUpdate({ helpText: e.target.value })}
              placeholder="Additional help text"
              rows={2}
            />
          </div>
        </div>

        <Separator />

        {/* Width */}
        <div className="space-y-2">
          <Label>Width</Label>
          <Select
            value={selectedField.width}
            onValueChange={(value) => handleUpdate({ width: value as FormField['width'] })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full">Full Width</SelectItem>
              <SelectItem value="half">Half Width</SelectItem>
              <SelectItem value="third">One Third</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator />

        {/* Options (for select, radio, etc.) */}
        {hasOptions && (
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Options</Label>
                <Button variant="outline" size="sm" onClick={handleAddOption}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {selectedField.options?.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={option.label}
                      onChange={(e) => handleUpdateOption(index, { label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      placeholder="Option label"
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* Validation */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-secondary-900 dark:text-white">
            Validation
          </h4>

          <div className="flex items-center justify-between">
            <Label>Required</Label>
            <Switch
              checked={isRequired}
              onCheckedChange={(checked) => {
                if (checked) {
                  addValidation(sectionId!, selectedField!.id, {
                    type: 'required',
                    message: 'This field is required',
                  });
                } else {
                  const index = selectedField!.validation.findIndex((v) => v.type === 'required');
                  if (index !== -1) {
                    removeValidation(sectionId!, selectedField!.id, index);
                  }
                }
              }}
            />
          </div>

          {['text', 'textarea'].includes(selectedField.type) && (
            <>
              <div className="space-y-2">
                <Label>Min Length</Label>
                <Input
                  type="number"
                  min={0}
                  value={
                    selectedField.validation.find((v) => v.type === 'minLength')?.value as number || ''
                  }
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value)) {
                      const existingIndex = selectedField!.validation.findIndex(
                        (v) => v.type === 'minLength'
                      );
                      if (existingIndex !== -1) {
                        removeValidation(sectionId!, selectedField!.id, existingIndex);
                      }
                      addValidation(sectionId!, selectedField!.id, {
                        type: 'minLength',
                        value,
                        message: `Must be at least ${value} characters`,
                      });
                    }
                  }}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label>Max Length</Label>
                <Input
                  type="number"
                  min={0}
                  value={
                    selectedField.validation.find((v) => v.type === 'maxLength')?.value as number || ''
                  }
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value)) {
                      const existingIndex = selectedField!.validation.findIndex(
                        (v) => v.type === 'maxLength'
                      );
                      if (existingIndex !== -1) {
                        removeValidation(sectionId!, selectedField!.id, existingIndex);
                      }
                      addValidation(sectionId!, selectedField!.id, {
                        type: 'maxLength',
                        value,
                        message: `Must be at most ${value} characters`,
                      });
                    }
                  }}
                  placeholder="No limit"
                />
              </div>
            </>
          )}

          {selectedField.type === 'number' && (
            <>
              <div className="space-y-2">
                <Label>Min Value</Label>
                <Input
                  type="number"
                  value={
                    selectedField.validation.find((v) => v.type === 'min')?.value as number || ''
                  }
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      const existingIndex = selectedField!.validation.findIndex(
                        (v) => v.type === 'min'
                      );
                      if (existingIndex !== -1) {
                        removeValidation(sectionId!, selectedField!.id, existingIndex);
                      }
                      addValidation(sectionId!, selectedField!.id, {
                        type: 'min',
                        value,
                        message: `Must be at least ${value}`,
                      });
                    }
                  }}
                  placeholder="No minimum"
                />
              </div>

              <div className="space-y-2">
                <Label>Max Value</Label>
                <Input
                  type="number"
                  value={
                    selectedField.validation.find((v) => v.type === 'max')?.value as number || ''
                  }
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      const existingIndex = selectedField!.validation.findIndex(
                        (v) => v.type === 'max'
                      );
                      if (existingIndex !== -1) {
                        removeValidation(sectionId!, selectedField!.id, existingIndex);
                      }
                      addValidation(sectionId!, selectedField!.id, {
                        type: 'max',
                        value,
                        message: `Must be at most ${value}`,
                      });
                    }
                  }}
                  placeholder="No maximum"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
